import { enqueue } from "@/lib/offlineQueue";
import { readSnapshotCollection, storeSnapshotCollection } from "@/lib/offlineSnapshot";
import { describeOfflineRequest } from "@/lib/offlineRoutes";
import { getHiveflowConfig } from "@/lib/environment";

const DEFERRED_MESSAGE = "Wordt uitgevoerd zodra er internet is";

function uuid() {
  return typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
    ? crypto.randomUUID()
    : `web-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function response(config, data, status = 200) {
  return { data, status, statusText: status === 202 ? "Accepted" : "OK", headers: {}, config, request: null };
}

function deferredServerActionError(config) {
  const error = new Error(DEFERRED_MESSAGE);
  error.code = "OFFLINE_SERVER_ACTION";
  error.isOfflineServerAction = true;
  error.config = config;
  error.response = { status: 503, data: { detail: DEFERRED_MESSAGE }, config };
  return error;
}

function idOf(item) {
  return item?.id ?? item?._offline?.serverId ?? item?._offline?.entityId;
}

function relation(item, snake, camel = null) {
  return item?.[snake] ?? item?.[camel || snake.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase())];
}

function isDeleted(item) {
  return Boolean(item?.isDeleted || item?._offline?.isDeleted || item?.deleted_at);
}

function filterItems(items, description) {
  let output = Array.isArray(items) ? items : [];
  if (description.onlyDeleted) output = output.filter(isDeleted);
  else output = output.filter((item) => !isDeleted(item));
  if (description.entityId) output = output.filter((item) => String(idOf(item)) === String(description.entityId));
  if (description.parentId) {
    const parentFields = description.entityType === "queen_history"
      ? ["queen_id", "queenId"]
      : description.entityType === "queen_cells"
      ? ["batch_id", "batchId"]
      : description.entityType === "weather"
      ? ["apiary_id", "apiaryId", "standId"]
      : ["parent_id", "parentId"];
    output = output.filter((item) => parentFields.some((field) => String(item?.[field] ?? "") === String(description.parentId)));
  }
  const filters = description.filters || {};
  if (filters.apiary_id) output = output.filter((item) => String(relation(item, "apiary_id", "standId") ?? "") === String(filters.apiary_id));
  if (filters.hive_id) output = output.filter((item) => String(relation(item, "hive_id") ?? "") === String(filters.hive_id));
  if (filters.status) output = output.filter((item) => item.status === filters.status);
  if (filters.status_filter && filters.status_filter !== "all") output = output.filter((item) => item.status === filters.status_filter);
  if (filters.start) output = output.filter((item) => !item.date || item.date >= filters.start);
  if (filters.end) output = output.filter((item) => !item.date || item.date <= filters.end);
  return output;
}

async function readCollection(entityType, options, bridge, requireBridge) {
  if (bridge?.nativeApp) {
    return bridge.getCollection(entityType, options);
  }
  if (requireBridge) throw new Error("Native offline-opslag is nog niet gereed");
  return readSnapshotCollection(entityType, {
    includeDeleted: options.includeDeleted,
    onlyDeleted: options.onlyDeleted,
  });
}

async function apiaryStats(description, bridge, requireBridge) {
  const apiaryId = description.entityId;
  const readLocal = (entityType, options = {}) => readCollection(entityType, options, bridge, requireBridge);
  const [apiaries, hivesAll, tasks, suggestions, inspections] = await Promise.all([
    readLocal("apiaries", { entityId: apiaryId, limit: 500 }),
    readLocal("hives", { filters: { apiary_id: apiaryId }, limit: 500 }),
    readLocal("tasks", { limit: 500 }),
    readLocal("suggestions", { limit: 500 }),
    readLocal("inspections", { limit: 500 }),
  ]);
  const apiary = filterItems(apiaries, { entityId: apiaryId });
  const hives = filterItems(hivesAll, { filters: { apiary_id: apiaryId } });
  const hiveIds = new Set(hives.map((item) => String(idOf(item))));
  const belongs = (item) => (
    String(relation(item, "apiary_id", "standId") ?? "") === String(apiaryId)
    || hiveIds.has(String(relation(item, "hive_id") ?? ""))
  );
  const relatedTasks = filterItems(tasks, {}).filter(belongs);
  const relatedSuggestions = filterItems(suggestions, {}).filter(belongs);
  const dates = filterItems(inspections, {}).filter(belongs).map((item) => item.date).filter(Boolean).sort().reverse();
  const today = new Date().toISOString().slice(0, 10);
  return {
    apiary: apiary[0] || null,
    hives,
    hive_count: hives.length,
    last_visit: dates[0] || null,
    open_tasks: relatedTasks.filter((item) => item.status === "open").length,
    overdue_tasks: relatedTasks.filter((item) => item.status === "open" && item.due_date && item.due_date < today).length,
    open_suggestions: relatedSuggestions.filter((item) => item.status === "open").length,
    high_suggestions: relatedSuggestions.filter((item) => item.status === "open" && item.urgency === "hoog").length,
  };
}

async function teamSnapshot(bridge, requireBridge) {
  const [organizations, members, invites] = await Promise.all([
    readCollection("organization", { limit: 10 }, bridge, requireBridge),
    readCollection("members", { limit: 1000 }, bridge, requireBridge),
    readCollection("invites", { limit: 1000 }, bridge, requireBridge),
  ]);
  let localUser = null;
  try { localUser = JSON.parse(localStorage.getItem("imker_user") || "null"); } catch {}
  const activeMembers = filterItems(members, {});
  const ownMember = activeMembers.find((item) => String(item.user_id ?? item.userId) === String(localUser?.id));
  return {
    organization: filterItems(organizations, {})[0] || { id: localUser?.organization_id || localUser?.id, name: "Mijn organisatie" },
    members: activeMembers,
    pending_invites: filterItems(invites, {}).filter((item) => !item.status || item.status === "pending"),
    my_role: ownMember?.role || localUser?.role || "member",
  };
}

const EXPORT_COLLECTIONS = {
  inspections: ["inspections"],
  tasks: ["tasks"],
  hives: ["hives"],
  queens: ["queens"],
  breeding: ["breeding_batches", "queen_cells", "mating_units"],
  "queen-history": ["queen_history"],
  "breeding-selection": ["queens", "breeding_batches"],
};

function csvCell(value) {
  const text = value == null ? "" : typeof value === "object" ? JSON.stringify(value) : String(value);
  return `"${text.replace(/"/g, '""')}"`;
}

async function exportSnapshot(type, bridge, requireBridge) {
  const collections = EXPORT_COLLECTIONS[type];
  if (!collections) throw new Error("Dit exporttype is offline niet beschikbaar");
  const rows = [];
  for (const entityType of collections) {
    const items = filterItems(await readCollection(entityType, { limit: 10000 }, bridge, requireBridge), {});
    for (const item of items) rows.push({ collection: entityType, ...item });
  }
  const columns = [...new Set(rows.flatMap((row) => Object.keys(row).filter((key) => key !== "_offline")))];
  const csv = `\uFEFF${columns.map(csvCell).join(",")}\r\n${rows.map((row) => columns.map((column) => csvCell(row[column])).join(",")).join("\r\n")}`;
  return new Blob([csv], { type: "text/csv;charset=utf-8" });
}

async function buildReadResponse(config, description, bridge, requireBridge) {
  if (description.responseKind === "apiaryStats") return response(config, await apiaryStats(description, bridge, requireBridge));
  if (description.responseKind === "team") return response(config, await teamSnapshot(bridge, requireBridge));
  if (description.responseKind === "export") return response(config, await exportSnapshot(description.exportType, bridge, requireBridge));

  const options = {
    entityId: description.entityId,
    parentId: description.parentId,
    filters: description.filters || {},
    includeDeleted: description.onlyDeleted === true,
    onlyDeleted: description.onlyDeleted === true,
    limit: 500,
  };
  const raw = await readCollection(description.entityType, options, bridge, requireBridge);
  const items = filterItems(raw, description);
  if (description.responseKind === "calendar") {
    return response(config, { start: description.filters?.start || null, end: description.filters?.end || null, items });
  }
  if (description.responseKind === "lineageOptions") return response(config, { queens: items });
  if (["entity", "singleton", "weather"].includes(description.responseKind)) return response(config, items[0] || null);
  return response(config, items);
}

export function cacheOnlineCollection(config, data) {
  const runtime = getHiveflowConfig();
  if (runtime.offlineBundle || (runtime.nativeBridgeEnabled && typeof window !== "undefined" && window.ReactNativeWebView)) return;
  if (String(config?.method || "get").toLowerCase() !== "get") return;
  const description = describeOfflineRequest(config || {});
  if (description.mode !== "read" || ["apiaryStats", "team", "export", "calendar", "lineageOptions"].includes(description.responseKind)) return;
  const cacheData = ["entity", "singleton", "weather"].includes(description.responseKind) && data && !Array.isArray(data)
    ? [{ ...data, id: data.id || description.entityType }]
    : data;
  storeSnapshotCollection(description.entityType, cacheData, {
    replace: Array.isArray(data) && !description.entityId && !description.parentId && !Object.values(description.filters || {}).some(Boolean),
  }).catch(() => {});
}

export function installOfflineTransport(config, forceOffline = false) {
  const bridge = typeof window !== "undefined" ? window.__hiveflowOfflineRepository : null;
  const fileBundle = getHiveflowConfig().offlineBundle;
  const offline = fileBundle || forceOffline || (bridge?.nativeApp ? !bridge.online : (typeof navigator !== "undefined" && !navigator.onLine));
  if (!offline) return config;

  const description = describeOfflineRequest(config);
  if (description.mode === "network") return config;
  if (description.mode === "server") {
    const online = bridge?.nativeApp ? bridge.online : (typeof navigator === "undefined" ? true : navigator.onLine);
    if (online) return config;
    config.adapter = async () => { throw deferredServerActionError(config); };
    return config;
  }
  if (description.mode === "static") {
    config.adapter = async () => response(config, description.value);
    return config;
  }
  if (description.mode === "unsupported") {
    if (fileBundle || forceOffline) {
      config.adapter = async () => { throw new Error("Deze functie is offline nog niet beschikbaar"); };
    }
    return config;
  }
  if (description.mode === "read") {
    config.adapter = () => buildReadResponse(config, description, bridge, fileBundle);
    return config;
  }

  config.adapter = async () => {
    const existingItems = description.entityId && !bridge?.nativeApp
      ? await readSnapshotCollection(description.entityType, { includeDeleted: true }).catch(() => [])
      : [];
    const existingEntity = existingItems.find((item) => String(idOf(item)) === String(description.entityId));
    const command = {
      actionType: description.actionType,
      entityType: description.entityType,
      entityId: description.entityId,
      entityServerId: description.entityId.startsWith("local-") ? null : description.entityId,
      operation: description.operation,
      payload: description.data,
      optimisticEntity: { ...(existingEntity || {}), ...description.data, id: description.entityId },
    };
    let optimisticEntity;
    if (bridge?.nativeApp) {
      const result = await bridge.queueCommand(command);
      optimisticEntity = result.optimisticEntity;
    } else {
      if (fileBundle) throw new Error("Native offline-opslag is nog niet gereed");
      await enqueue({ kind: "command", clientActionId: uuid(), ...command });
      optimisticEntity = { ...command.optimisticEntity, syncStatus: "pending" };
    }
    window.dispatchEvent(new CustomEvent("hiveflow:offline-command-queued", { detail: { message: DEFERRED_MESSAGE } }));
    return response(config, { ...optimisticEntity, offlineMessage: DEFERRED_MESSAGE }, 202);
  };
  return config;
}

export async function recoverWithOfflineTransport(error) {
  const config = error?.config;
  const bridge = typeof window !== "undefined" ? window.__hiveflowOfflineRepository : null;
  const recoverable = !error?.response || Number(error.response?.status) >= 500;
  const isRead = String(config?.method || "get").toLowerCase() === "get";
  if (!config || config._offlineRecovery || !recoverable || (!bridge?.nativeApp && !isRead)) throw error;
  const retryConfig = installOfflineTransport({ ...config, _offlineRecovery: true }, true);
  if (typeof retryConfig.adapter !== "function") throw error;
  return retryConfig.adapter(retryConfig);
}

export { DEFERRED_MESSAGE };
